import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useBlocker,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { Editor } from "tldraw";

import { ErrorBanner } from "@/components/Callout";
import {
  CustomNodeEditorContext,
  defaultCustomNodeFeedVersion,
} from "@/components/canvas-editor/utils";
import { CustomNodeColorSelect } from "@/components/CustomNodeColorSelect";
import { FeedEditor, FeedEditorHandle } from "@/components/FeedEditor";
import { FeedForm } from "@/components/FeedForm";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Button, ButtonLink } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { H1 } from "@/components/ui/typography";
import {
  createCustomNode,
  getCustomNodeQueryOptions,
  getUserCustomNodesQueryOptions,
  searchCustomNodesQueryOptions,
} from "@/lib/api";
import { getSnapshot } from "@/lib/getSnapshot";
import { FeedVersion } from "@/lib/types";

export const Route = createFileRoute(
  "/app/_sidebar-layout/custom-nodes/create",
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): { remix?: number } => {
    // validate and parse the search params into a typed state
    return {
      remix: search?.remix ? parseInt(search?.remix as string) : undefined,
    };
  },
  loader: async ({ location, context: { queryClient } }) => {
    void queryClient.ensureQueryData(getUserCustomNodesQueryOptions());
    void queryClient.prefetchInfiniteQuery(
      searchCustomNodesQueryOptions({ query: "", favorited_only: true }),
    );

    await Promise.all([
      "remix" in location.search
        ? queryClient.ensureQueryData(
            getCustomNodeQueryOptions(location.search.remix as number),
          )
        : undefined,
    ]);
  },
  head: () => ({ meta: [{ title: "Create Custom Node" }] }),
});

function RouteComponent() {
  // Publish the feed

  const location = useLocation();
  const { data: remixNode } = location.search.remix
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseQuery(getCustomNodeQueryOptions(location.search.remix))
    : { data: undefined };

  const [feedVersion, setFeedVersion] = useState<
    Omit<FeedVersion, "created_at"> | undefined
  >(
    remixNode
      ? {
          ...defaultCustomNodeFeedVersion,
          manifest: {
            filter: remixNode.component,
          },
        }
      : defaultCustomNodeFeedVersion,
  );
  const [description, setDescription] = useState("");
  const navigate = useNavigate({ from: "/app/custom-nodes/create" });
  const feedEditorRef = useRef<FeedEditorHandle>(null!);
  const editorRef = useRef<Editor>(null!);
  const shouldBlock = useRef(true);
  const onSubmit = useMutation({
    mutationFn: async (data: FormData) => {
      const filter = feedVersion?.manifest.filter;

      if (!filter) {
        throw new AxiosError("No filter found");
      }

      const node = await createCustomNode({
        description,
        title: data.get("display_name") as string,
        public: data.get("public") === "on",
        color: data.get("color") as string,
        component: feedVersion?.manifest.filter,
        component_metadata: {
          snapshot: getSnapshot(feedVersion),
        },
      });

      return node;
    },
    onSuccess: async (node) => {
      shouldBlock.current = false;
      await navigate({
        to: "/app/custom-nodes/$nodeId/view",
        params: { nodeId: `${node.id}` },
      });
    },
    onError: (error: AxiosError) => console.error(error),
  });

  useBlocker({
    shouldBlockFn: (event) => {
      if (event.action === "REPLACE") {
        return false;
      }

      if (shouldBlock.current) {
        const shouldNav = confirm(
          "Are you sure you want to leave? This will discard all your changes.",
        );
        return !shouldNav;
      }

      return false;
    },
  });

  return (
    <FeedForm
      isCreate={true}
      onSubmit={onSubmit.mutate}
      feedVersion={feedVersion}
      feedEditorRef={feedEditorRef}
      editorRef={editorRef}
    >
      {onSubmit.error && <ErrorBanner error={onSubmit.error} />}

      <div className="flex gap-4 border-b-2 border-slate-7 dark:border-slatedark-7 pb-4 self-stretch">
        <H1 className="border-b-0 flex-1">
          <input
            required
            name="display_name"
            placeholder="Node Name"
            className="w-full bg-transparent placeholder:text-slate-dim focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg"
            autoComplete="off"
          />
        </H1>
      </div>

      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <Label htmlFor="public-mode">Make public</Label>
            <div className="text-sm text-slate-dim">
              Set whether the node is public so other users can use it in their
              feeds.
            </div>
          </div>
        </div>
        <Switch name="public" id="public-mode" defaultChecked={true} />
      </div>

      <CustomNodeColorSelect editorRef={editorRef} />

      <div className="flex flex-col gap-3">
        <Label htmlFor="description">Node Description</Label>
        <RichTextEditor
          placeholder="Describe your node"
          id="description"
          value={description}
          onChange={setDescription}
          variant="full"
        />
      </div>

      <CustomNodeEditorContext value={true}>
        <FeedEditor
          handle={feedEditorRef}
          editorRef={editorRef}
          feedVersion={feedVersion}
          onChange={setFeedVersion}
          fullscreenActions={
            <Button size="sm" isLoading={onSubmit.isPending} type="submit">
              Create
            </Button>
          }
          centerContent={true}
          rootNode={{
            title: "Custom Node",
            variant: "custom-node",
            description:
              "This is a definition of a custom node that you can share with other feeds.",
            customNodeParameters:
              feedVersion?.manifest.filter.metadata?.customNodeParameters,
          }}
        />
      </CustomNodeEditorContext>

      <div className="flex justify-between gap-4">
        <div className="flex-1" />
        <div className="flex gap-2">
          <ButtonLink size="lg" variant="ghost" to="/app">
            Cancel
          </ButtonLink>
          <Button size="lg" isLoading={onSubmit.isPending} type="submit">
            Publish
          </Button>
        </div>
      </div>
    </FeedForm>
  );
}
